import React, { useMemo, useState } from "react";
import { useCompanyId, useCompanyName } from "src/Pages/Company/CompanyContext";
import { Button } from "@mui/material";
import { useFetchStructureData } from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/OrganizationStructure/api/useFetchStructureData";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import useIsAhr from "src/utils/hooks/useIsAhr";
import { getCompanyBaseBreadcrumbs } from "src/Pages/Company/routerHelper";
import __ from "src/utils/Translations";
import { handleDownload } from "src/Pages/Company/EmployeeManagement/DataManagment/Utils/handleDownload";
import {
  ContentType,
  DataSource,
} from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureAreas/DataManagment/DataManagment.models";
import DownloadHistory, {
  ImportEntry,
} from "src/Pages/Company/OrganizationStructure/sharedComponents/DownloadHistory/DownloadHistory";
import {
  importEmployeeData,
  importEmployeeStructureData,
} from "src/Pages/Company/OrganizationStructure/api";
import useImportData from "src/Pages/Company/OrganizationStructure/api/useImportData";
import useImportModal from "src/Pages/Company/OrganizationStructure/sharedComponents/useImportModal";
import ImportExportLayout from "src/Pages/Company/OrganizationStructure/Layouts/ImportExportLayout";
import BackButton from "src/Pages/Company/OrganizationStructure/sharedComponents/BackButton";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import {
  organizationStructureEmployeeDataExport,
  organizationStructureEmployeeDataImport,
  organizationStructureEmployeeDataRead,
  organizationStructureLocalizationImportHistoryRead,
  organizationStructurePositionImportHistoryRead,
} from "src/utils/RoleBasedSecurity/permissions";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import FEATURE_FLAGS from "src/utils/constants/featureFlags";
import {
  getEmployeeImportInstructions,
  getEmployeeUpdateFields,
} from "./constants";

interface EmployeeProcessData {
  authorId: string;
  id: string;
  status: string;
}

const EMPLOYEE_FIELDS = () => [
  __("Numer MPK"),
  __("Adres email służbowy"),
  __("Telefon służbowy"),
  __("Telefon służbowy 2"),
];

const STRUCTURE_FIELDS = () => [
  __("Stanowisko"),
  __("Obszar"),
  __("Wyświetlany dodatkowy obszar"),
  __("Przełożony"),
  __("Lokalizacja"),
  __("Data rozpoczęcia zatrudnienia"),
  __("Data zakończenia zatrudnienia"),
  __("Rodzaj umowy"),
  __("Forma zatrudnienia"),
  __("Wymiar etatu"),
  __("Godzinowy wymiar pracy"),
];

const DataManagement = () => {
  const companyId = useCompanyId();
  const { data, isFetching } = useFetchStructureData(companyId);
  const isAhr = useIsAhr();
  const companyName = useCompanyName();

  const [processDataEmployee, setProcessDataEmployee] = useState<
    EmployeeProcessData | undefined
  >(undefined);
  const [processDataStructure, setProcessDataStructure] = useState<
    { id: string } | undefined
  >(undefined);

  const canExportEmployeeData = usePermission(
    organizationStructureEmployeeDataExport
  );
  const canImportEmployeeData = usePermission(
    organizationStructureEmployeeDataImport
  );
  const hasOrganizationStructurePositionImportHistoryRead = usePermission(
    organizationStructurePositionImportHistoryRead
  );
  const hasOrganizationStructureLocalizationImportHistoryRead = usePermission(
    organizationStructureLocalizationImportHistoryRead
  );
  const hasOrganizationStructureEmployeeDataRead = usePermission(
    organizationStructureEmployeeDataRead
  );
  const canPreviewImportHistory =
    hasOrganizationStructureEmployeeDataRead
    || hasOrganizationStructurePositionImportHistoryRead
    || hasOrganizationStructureLocalizationImportHistoryRead;

  const hasImportEmployeesEnabled = useFeatureIsOn(
    FEATURE_FLAGS.ORGANIZATION_STRUCTURE_IMPORT_EMPLOYEES
  );
  const hasExportEmployeesEnabled = useFeatureIsOn(
    FEATURE_FLAGS.ORGANIZATION_STRUCTURE_EXPORT_EMPLOYEES
  );

  const structureId = isFetching ? "" : data?.[0]?.structureId;

  const { mutate: importEmployee } = useImportData<EmployeeProcessData>({
    mutationFn: (file: File) => importEmployeeData({ companyId, file }),
    successMessage: __("Pomyślnie zapisano dane"),
    errorMessage: __("Nie udało się zapisać danych"),
    onImportSuccess: (data) => {
      if (data?.authorId && data?.id && data?.status) {
        setProcessDataEmployee(data);
      }
    },
  });

  const { mutate: importStructure } = useImportData({
    mutationFn: (file: File) =>
      importEmployeeStructureData({
        companyId,
        structureId: structureId as string,
        file,
      }),
    successMessage: __("Pomyślnie zaimportowano pracowników"),
    errorMessage: __("Nie udało się zaimportować pracowników"),
    onImportSuccess: (data) => setProcessDataStructure(data),
  });

  const { open: openImportModal } = useImportModal({
    title: __("Import danych pracowników"),
    mutates: [importEmployee, importStructure],
  });

  const instructions = getEmployeeImportInstructions();
  const chipLabels = getEmployeeUpdateFields();

  const baseBreadcrumbs = (
    getCompanyBaseBreadcrumbs(companyId, companyName) as {
      title: string;
      link: string;
    }[]
  ).map(({ title, link }) => ({
    label: title,
    to: link,
  }));

  const breadcrumbs = isAhr
    ? [
        {
          label: `${__("Zarządzanie firmą")} ${companyName}`,
          to: `/ahr`,
        },
        {
          label: __("Eksport i import danych pracowników"),
          to: `/ahr/import-export-data`,
        },
      ]
    : [
        ...baseBreadcrumbs,
        {
          label: __("Eksport i import danych pracowników"),
          to: `/company/edit/${companyId}/employee-management/import-export-data`,
        },
      ];

  const imports = useMemo<ImportEntry[]>(() => {
    const entries: ImportEntry[] = [];
    if (processDataEmployee) {
      entries.push({
        processData: processDataEmployee,
        fields: EMPLOYEE_FIELDS(),
      });
    }
    if (processDataStructure) {
      entries.push({
        processData: processDataStructure,
        fields: STRUCTURE_FIELDS(),
      });
    }
    return entries;
  }, [processDataEmployee, processDataStructure]);

  return (
    <>
      <BreadcrumbsMui items={breadcrumbs} />
      <BackButton sx={{ mt: 4, mb: 3, p: 0 }} />
      <ImportExportLayout>
        <ImportExportLayout.Header
          title={__("Eksport i import danych pracowników")}
          description={__(
            "Na tej stronie możesz eksportować dane pracowników, wprowadzić w nich zmiany w pliku Excel, a następnie zaimportować zaktualizowane informacje z powrotem do systemu."
          )}
        />

        {canExportEmployeeData && hasExportEmployeesEnabled && (
          <>
            <ImportExportLayout.SectionHeader
              title={__("Eksport danych pracowników w Twojej firmie")}
            />
            <ImportExportLayout.ActionCard
              title={__("Eksportuj do pliku")}
              description={__("Eksportuj do pliku dane swoich pracowników.")}
              actions={
                <>
                  <Button
                    variant="outlined"
                    onClick={() =>
                      handleDownload({
                        companyId,
                        structureId: structureId as string,
                        contentType: ContentType.CSV,
                        dataSource: DataSource.STRUCTURES,
                      })
                    }
                  >
                    {__("Eksportuj plik CSV")}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() =>
                      handleDownload({
                        companyId,
                        structureId: structureId as string,
                        contentType: ContentType.XLSX,
                        dataSource: DataSource.STRUCTURES,
                      })
                    }
                  >
                    {__("Eksportuj plik XLSX")}
                  </Button>
                </>
              }
            />

            <ImportExportLayout.Divider />
          </>
        )}

        <ImportExportLayout.SectionHeader title={__("Szablon do importów")} />
        <ImportExportLayout.ActionCard
          title={__("Pobierz szablon do importów")}
          description={__(
            "Pobrany szablon możesz wykorzystać do przygotowania importu pracownika"
          )}
          actions={
            <>
              <Button
                variant="outlined"
                onClick={() =>
                  handleDownload({
                    companyId,
                    structureId: structureId as string,
                    contentType: ContentType.CSV,
                    dataSource: DataSource.STRUCTURES,
                    template: true,
                  })
                }
              >
                {__("Pobierz szablon CSV")}
              </Button>
              <Button
                variant="outlined"
                onClick={() =>
                  handleDownload({
                    companyId,
                    structureId: structureId as string,
                    contentType: ContentType.XLSX,
                    dataSource: DataSource.STRUCTURES,
                    template: true,
                  })
                }
              >
                {__("Pobierz szablon XLSX")}
              </Button>
            </>
          }
        />

        <ImportExportLayout.Divider />

        {canImportEmployeeData && hasImportEmployeesEnabled && (
          <>
            <ImportExportLayout.SectionHeader
              title={__("Import danych pracowników")}
            />
            <ImportExportLayout.Instructions instructions={instructions} />
            <ImportExportLayout.ActionCard
              title={__("Importuj dane pracowników")}
              description={__("Wgraj plik .csv lub .xlsx")}
              actions={
                <Button variant="outlined" onClick={openImportModal}>
                  {__("Importuj")}
                </Button>
              }
              metadata={{
                label: __("Dane aktualizowane podczas tego importu"),
                chips: chipLabels,
              }}
            />
          </>
        )}

        {canPreviewImportHistory && <DownloadHistory imports={imports} />}
      </ImportExportLayout>
    </>
  );
};

export default DataManagement;
